import { createStore } from 'zustand/vanilla'
import { hasFeature, upgradeCopy } from './features'

/**
 * UI state: which modal is open, the config dialog's initial tab and focus,
 * the expanded menu groups, the data-modal focus, the guided-tour counter.
 * Generated from the Pinia store's own source in Phase 1B — the 34 modal
 * names, 34 showX getters and 73 actions are the ones ui.js exported.
 */
export const MODAL_NAMES = [
  'scenarios',
  'scenariosLibrary',
  'unifiedConfig',
  'customizeMap',
  'dataManagement',
  'dataCleaning',
  'dataAggregation',
  'whatIfAnalysis',
  'reporting',
  'pptExport',
  'demandAnalysis',
  'capacityUtilization',
  'purchaseMethod',
  'addNode',
  'helpDialog',
  'presentationMode',
  'negotiation',
  'shippingCost',
  'fixedCost',
  'variableCost',
  'totalCost',
  'riskDetail',
  'dataValidation',
  'sourceNode',
  'customerNode',
  'intermediaryNode',
  'lane',
  'aiChat',
  'monteCarloResults',
  'monteCarloProgress',
  'scenarioComparisonBar',
  'upgradeModal',
  'menuPanel',
] as const
export type ModalName = (typeof MODAL_NAMES)[number]

export type UiState = {
  modals: Record<ModalName, boolean>
  unifiedConfigInitialTab: string
  unifiedConfigFocus: string | null
  dataModalFocus: string | null
  guidedTourRequests: number
  /**
   * Chrome state MapView.vue owned as local refs until Phase 1D. It moved here
   * so a command — and therefore React — can read and change it without a Vue
   * component in the way. The two quick-filter flags keep the exact
   * localStorage keys and the 'true'/'false' encoding MapView's persisted refs
   * used, so a returning user's saved layout still applies.
   */
  statsCustomization: boolean
  quickFilterBar: boolean
  quickFilterCollapsed: boolean
  customizeMapInitialTab: string | null
  /** The feature the user is in, for the drawer's "you are here" row. */
  activeMenuFeature: string | null
  /** A run is in flight. The toolbar's buttons read it; a command sets it. */
  optimizing: boolean
  /**
   * A Monte Carlo run is in flight.
   *
   * The same contract as `optimizing`, and it should have arrived with it in
   * Phase 1D. It did not, so the Run Simulation button's loading state stayed a
   * bare `ref(false)` inside `useMonteCarloHandlers` — invisible to anything
   * outside that composable, and therefore to any React toolbar.
   */
  simulating: boolean
  /**
   * The bottom edge of the fixed chrome (app bar + stat bar), in viewport px.
   *
   * MapView lays the Quick Filters panel, `--chrome-bottom` and the map's top
   * against it. It used to MEASURE this out of `statsBarRef.value.$el`, which
   * stops existing the moment the bar is not a Vue component — so the bar
   * publishes it instead (Phase 1G). Null until the first measurement.
   */
  chromeBottom: number | null
  /**
   * What the upgrade modal is about. Set by `requireFeature`, read by the modal.
   *
   * These were three refs created fresh inside every `useFeatureGating()` call,
   * so only the one instance MapView happened to destructure was bound to the
   * modal — a second caller's `requireFeature` set refs nothing rendered, which
   * is why `optimization.ts` had to take `requireFeature` as a parameter.
   */
  lockedFeature: string
  requiredTier: string
  /**
   * The node the open node modal is about.
   *
   * This lived as SIX refs in MapView.vue, declared there so that
   * `useMapRendering` (whose Leaflet click handlers write them) and
   * `useNodeManagement` (which hands them to the modals as props) would share
   * them — and they shared them only because both composables happened to be
   * called once, in one component. `useNodeManagement` even has a
   * `|| ref(null)` fallback, so a second caller silently gets its own
   * disconnected copy.
   *
   * That is the same shape as `lockedFeature` above, and it is why the map
   * cannot move to React while the modals stay Vue: the click would write refs
   * the modals do not read. Owning it here makes the two sides independent.
   */
  selectedNode: SelectedNode | null
}

/** Which node modal a selection belongs to. */
export type NodeKind = 'source' | 'customer' | 'intermediary'

export type SelectedNode = { kind: NodeKind; id: unknown; name: unknown }

const NODE_MODAL: Record<NodeKind, ModalName> = {
  source: 'sourceNode',
  customer: 'customerNode',
  intermediary: 'intermediaryNode',
}

const QUICK_FILTER_VISIBLE_KEY = 'quickFilterBarVisible'
const QUICK_FILTER_COLLAPSED_KEY = 'quickFilterBarCollapsed'

function savedBoolean(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key)
    return value === null ? fallback : value === 'true'
  } catch {
    return fallback
  }
}

function persistBoolean(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? 'true' : 'false')
  } catch {
    /* storage unavailable: in-memory only, as before */
  }
}

function defaults(): UiState {
  return {
    modals: Object.fromEntries(MODAL_NAMES.map((n) => [n, false])) as Record<ModalName, boolean>,
    unifiedConfigInitialTab: 'network',
    unifiedConfigFocus: null,
    dataModalFocus: null,
    guidedTourRequests: 0,
    statsCustomization: false,
    quickFilterBar: savedBoolean(QUICK_FILTER_VISIBLE_KEY, true),
    quickFilterCollapsed: savedBoolean(QUICK_FILTER_COLLAPSED_KEY, false),
    customizeMapInitialTab: null,
    activeMenuFeature: null,
    optimizing: false,
    simulating: false,
    chromeBottom: null,
    lockedFeature: '',
    requiredTier: 'essential',
    selectedNode: null,
  }
}

export const uiStore = createStore<UiState>(() => defaults())

function setModal(name: ModalName, open: boolean) {
  if (!(name in uiStore.getState().modals)) return // unknown names are ignored, as before
  uiStore.setState((s) => ({ modals: { ...s.modals, [name]: open } }))
}

export const uiSelectors = {
  showNegotiation: (s: UiState) => s.modals.negotiation,
  showScenariosModal: (s: UiState) => s.modals.scenarios,
  showScenariosLibraryModal: (s: UiState) => s.modals.scenariosLibrary,
  showUnifiedConfigModal: (s: UiState) => s.modals.unifiedConfig,
  showCustomizeMapModal: (s: UiState) => s.modals.customizeMap,
  showDataModal: (s: UiState) => s.modals.dataManagement,
  showDataCleaningModal: (s: UiState) => s.modals.dataCleaning,
  showDataAggregationModal: (s: UiState) => s.modals.dataAggregation,
  showWhatIfAnalysisModal: (s: UiState) => s.modals.whatIfAnalysis,
  showReportingModal: (s: UiState) => s.modals.reporting,
  showPPTModal: (s: UiState) => s.modals.pptExport,
  showDemandAnalysisModal: (s: UiState) => s.modals.demandAnalysis,
  showPurchaseMethodModal: (s: UiState) => s.modals.purchaseMethod,
  showAddNodeModal: (s: UiState) => s.modals.addNode,
  showHelpDialog: (s: UiState) => s.modals.helpDialog,
  showPresentationModePanel: (s: UiState) => s.modals.presentationMode,
  showShippingCostModal: (s: UiState) => s.modals.shippingCost,
  showFixedCostModal: (s: UiState) => s.modals.fixedCost,
  showVariableCostModal: (s: UiState) => s.modals.variableCost,
  showTotalCostModal: (s: UiState) => s.modals.totalCost,
  showCapacityUtilizationModal: (s: UiState) => s.modals.capacityUtilization,
  showRiskDetailModal: (s: UiState) => s.modals.riskDetail,
  showDataValidationModal: (s: UiState) => s.modals.dataValidation,
  /** The selected node's id and name, but only for the modal it belongs to. */
  selectedNodeOf: (s: UiState, kind: NodeKind) =>
    (s.selectedNode?.kind === kind ? s.selectedNode : null),
  showSourceNodeModal: (s: UiState) => s.modals.sourceNode,
  showCustomerNodeModal: (s: UiState) => s.modals.customerNode,
  showIntermediaryNodeModal: (s: UiState) => s.modals.intermediaryNode,
  showLaneModal: (s: UiState) => s.modals.lane,
  showAIChat: (s: UiState) => s.modals.aiChat,
  showMonteCarloResults: (s: UiState) => s.modals.monteCarloResults,
  showMonteCarloProgress: (s: UiState) => s.modals.monteCarloProgress,
  showScenarioComparisonBar: (s: UiState) => s.modals.scenarioComparisonBar,
  showUpgradeModal: (s: UiState) => s.modals.upgradeModal,
  showMenuPanel: (s: UiState) => s.modals.menuPanel,
}

export const uiActions = {
  openModal: (name: ModalName) => setModal(name, true),
  closeModal: (name: ModalName) => setModal(name, false),
  toggleModal: (name: ModalName) => setModal(name, !uiStore.getState().modals[name]),
  closeAllModals: () =>
    uiStore.setState((s) => ({
      modals: Object.fromEntries(Object.keys(s.modals).map((n) => [n, false])) as Record<ModalName, boolean>,
    })),
  openConfigModal(tab = 'network', focus: string | null = null) {
    // Cleared even when null, so a plain open after a focused one does not
    // inherit the previous target and scroll somewhere unasked.
    uiStore.setState({ unifiedConfigInitialTab: tab, unifiedConfigFocus: focus })
    setModal('unifiedConfig', true)
  },
  openDataModal(focus: string | null = null) {
    uiStore.setState({ dataModalFocus: focus })
    setModal('dataManagement', true)
  },
  startGuidedTour: () => uiStore.setState((s) => ({ guidedTourRequests: s.guidedTourRequests + 1 })),
  /** Replaces `uiStore.modals.scenarioComparisonBar.value = enabled` — `.value` on an unwrapped boolean never did anything. */
  setScenarioComparisonBar: (enabled: boolean) => setModal('scenarioComparisonBar', enabled),
  openNegotiation: () => setModal('negotiation', true),
  closeNegotiation: () => setModal('negotiation', false),
  openScenariosModal: () => setModal('scenarios', true),
  closeScenariosModal: () => setModal('scenarios', false),
  openScenariosLibrary: () => setModal('scenariosLibrary', true),
  closeScenariosLibrary: () => setModal('scenariosLibrary', false),
  closeConfigModal: () => setModal('unifiedConfig', false),
  openOptimizationConfig: () => uiActions.openConfigModal('optimization'),
  openSimulationConfig: () => uiActions.openConfigModal('simulation'),
  /** A plain open clears the tab, so a previous focused open cannot leak into it. */
  openCustomizeMap() {
    uiStore.setState({ customizeMapInitialTab: null })
    setModal('customizeMap', true)
  },
  /**
   * The menu's "Presentation Mode" item: the Customize Map modal, opened on its
   * presentation tab. Named for what it does, because `openPresentationMode`
   * below is a different thing — a modal with no reader anywhere in the app.
   */
  openCustomizeMapOnPresentation() {
    uiStore.setState({ customizeMapInitialTab: 'presentation' })
    setModal('customizeMap', true)
  },
  setOptimizing: (running: boolean) => uiStore.setState({ optimizing: running }),
  setSimulating: (running: boolean) => uiStore.setState({ simulating: running }),
  /**
   * Publish the chrome's bottom edge. Ignores non-edges (0 from a hidden
   * element, NaN from a detached one) and sub-pixel jitter, because every write
   * re-lays-out MapView and re-invalidates Leaflet.
   */
  setChromeBottom(bottom: number) {
    if (!Number.isFinite(bottom) || bottom <= 0) return
    const current = uiStore.getState().chromeBottom
    if (current !== null && Math.abs(current - bottom) < 1) return
    uiStore.setState({ chromeBottom: bottom })
  },
  /**
   * There is no chrome to lay out against — the bar is hidden or gone. Not the
   * same as a 0 edge, which `setChromeBottom` rejects as a non-measurement:
   * without this, hiding the bar left the page laid out against a band that is
   * no longer on screen, and a remount laid the map out against the previous
   * mount's edge before its own bar existed.
   */
  clearChromeBottom() {
    if (uiStore.getState().chromeBottom === null) return
    uiStore.setState({ chromeBottom: null })
  },
  /**
   * Gate an action. Returns true if it may proceed; otherwise opens the upgrade
   * modal, naming the feature and the tier it needs, and returns false.
   */
  requireFeature(featureName: string, displayName: string | null = null): boolean {
    if (hasFeature(featureName)) return true
    const { lockedFeature, requiredTier } = upgradeCopy(featureName, displayName)
    uiStore.setState({ lockedFeature, requiredTier })
    setModal('upgradeModal', true)
    return false
  },
  openStatsCustomization: () => uiStore.setState({ statsCustomization: true }),
  closeStatsCustomization: () => uiStore.setState({ statsCustomization: false }),
  /** Hiding the bar also clears the collapse, as useFilterHandlers did. */
  setQuickFilterBar(visible: boolean) {
    uiStore.setState(visible ? { quickFilterBar: true } : { quickFilterBar: false, quickFilterCollapsed: false })
    persistBoolean(QUICK_FILTER_VISIBLE_KEY, visible)
    if (!visible) persistBoolean(QUICK_FILTER_COLLAPSED_KEY, false)
  },
  setQuickFilterCollapsed(collapsed: boolean) {
    uiStore.setState({ quickFilterCollapsed: collapsed })
    persistBoolean(QUICK_FILTER_COLLAPSED_KEY, collapsed)
  },
  closeCustomizeMap: () => setModal('customizeMap', false),
  closeDataModal: () => setModal('dataManagement', false),
  openDataCleaning: () => setModal('dataCleaning', true),
  closeDataCleaning: () => setModal('dataCleaning', false),
  openDataAggregation: () => setModal('dataAggregation', true),
  closeDataAggregation: () => setModal('dataAggregation', false),
  openWhatIfAnalysis: () => setModal('whatIfAnalysis', true),
  closeWhatIfAnalysis: () => setModal('whatIfAnalysis', false),
  openReporting: () => setModal('reporting', true),
  closeReporting: () => setModal('reporting', false),
  openPPTExport: () => setModal('pptExport', true),
  closePPTExport: () => setModal('pptExport', false),
  openDemandAnalysis: () => setModal('demandAnalysis', true),
  closeDemandAnalysis: () => setModal('demandAnalysis', false),
  openPurchaseMethodAnalysis: () => setModal('purchaseMethod', true),
  closePurchaseMethodAnalysis: () => setModal('purchaseMethod', false),
  openAddNode: () => setModal('addNode', true),
  openAddNodeModal: () => uiActions.openAddNode(),
  closeAddNode: () => setModal('addNode', false),
  openHelpDialog: () => setModal('helpDialog', true),
  closeHelpDialog: () => setModal('helpDialog', false),
  openPresentationMode: () => setModal('presentationMode', true),
  closePresentationMode: () => setModal('presentationMode', false),
  openShippingCostModal: () => setModal('shippingCost', true),
  openShippingCostAnalysis: () => uiActions.openShippingCostModal(),
  closeShippingCostModal: () => setModal('shippingCost', false),
  openFixedCostModal: () => setModal('fixedCost', true),
  openFixedCostAnalysis: () => uiActions.openFixedCostModal(),
  closeFixedCostModal: () => setModal('fixedCost', false),
  openVariableCostModal: () => setModal('variableCost', true),
  openVariableCostAnalysis: () => uiActions.openVariableCostModal(),
  closeVariableCostModal: () => setModal('variableCost', false),
  openTotalCostModal: () => setModal('totalCost', true),
  openTotalCostAnalysis: () => uiActions.openTotalCostModal(),
  closeTotalCostModal: () => setModal('totalCost', false),
  openCapacityUtilizationModal: () => setModal('capacityUtilization', true),
  openCapacityUtilizationAnalysis: () => uiActions.openCapacityUtilizationModal(),
  closeCapacityUtilizationModal: () => setModal('capacityUtilization', false),
  openRiskAnalysis: () => setModal('riskDetail', true),
  closeRiskAnalysis: () => setModal('riskDetail', false),
  openDataValidation: () => setModal('dataValidation', true),
  closeDataValidation: () => setModal('dataValidation', false),
  /**
   * Open the node modal for the facility that was just clicked.
   *
   * One call, because the two halves must not drift: setting the selection
   * without opening leaves a stale id behind the next open, and opening
   * without setting shows the PREVIOUS facility's details — which is what
   * separate refs and a separate `open*` call made possible.
   */
  openNodeModal(kind: NodeKind, id: unknown, name: unknown) {
    uiStore.setState({ selectedNode: { kind, id, name } })
    setModal(NODE_MODAL[kind], true)
  },
  openSourceNodeModal: () => setModal('sourceNode', true),
  closeSourceNodeModal: () => setModal('sourceNode', false),
  openCustomerNodeModal: () => setModal('customerNode', true),
  closeCustomerNodeModal: () => setModal('customerNode', false),
  openIntermediaryNodeModal: () => setModal('intermediaryNode', true),
  closeIntermediaryNodeModal: () => setModal('intermediaryNode', false),
  openLaneModal: () => setModal('lane', true),
  closeLaneModal: () => setModal('lane', false),
  openAIChat: () => setModal('aiChat', true),
  closeAIChat: () => setModal('aiChat', false),
  openMenuPanel: () => setModal('menuPanel', true),
  /**
   * Remember which feature the drawer just sent the user into.
   *
   * Two items can name the same feature — Import Data and Load Mock Network are
   * both `data-management` — so this is the feature, not the item.
   */
  setActiveMenuFeature: (feature: string | null) => uiStore.setState({ activeMenuFeature: feature }),
  closeMenuPanel: () => setModal('menuPanel', false),
}

export function __resetUiForTests() {
  uiStore.setState(defaults(), true)
}
